import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Users, FolderOpen, FileText, Calendar, Filter, ArrowRight } from "lucide-react";
import { useModalities } from "@/contexts/ModalitiesContext";
import { getModalityBadgeClass } from "@/lib/modalities";
import ModalityDotLabel from "@/components/ModalityDotLabel";
import { formatPatientDisplayUhid } from "@/lib/patientDisplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const navigate = useNavigate();
  const { modalities } = useModalities();
  const [query, setQuery] = useState("");
  const [modality, setModality] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tag, setTag] = useState("");
  const [tags, setTags] = useState([]);
  const [entityType, setEntityType] = useState("all");
  const [results, setResults] = useState({ patients: [], studies: [], files: [] });
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await axios.get(`${API}/patients/ref/tags`);
        const list = Array.isArray(res.data?.tags)
          ? res.data.tags
          : Array.isArray(res.data)
            ? res.data
            : [];
        setTags(list.filter(Boolean).map(String));
      } catch (e) {
        console.error(e);
        setTags([]);
      }
    };
    fetchTags();
  }, []);

  const handleSearch = async () => {
    if (!query && !modality && !dateFrom && !dateTo && !tag) return;
    setLoading(true);
    try {
      const params = { q: query, entity_type: entityType };
      if (modality) params.modality = modality;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (tag) params.tag = tag;
      const res = await axios.get(`${API}/search`, { params });
      setResults(res.data);
      setSearched(true);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const totalResults = results.patients.length + results.studies.length + results.files.length;

  return (
    <div className="space-y-6" data-testid="search-page">
      {/* Search Form */}
      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="search-query"
                placeholder="Search patients, studies, findings, filenames..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="pl-9 h-10"
              />
            </div>
            <Button data-testid="search-submit" onClick={handleSearch} disabled={loading} className="gap-2 px-6 w-full sm:w-auto">
              <Search className="w-4 h-4" /> {loading ? "Searching..." : "Search"}
            </Button>
          </div>

          {/* Advanced Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger data-testid="search-entity-type" className="w-32 h-8 text-xs"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="patients">Patients</SelectItem>
                <SelectItem value="studies">Studies</SelectItem>
                <SelectItem value="files">Files</SelectItem>
              </SelectContent>
            </Select>
            <Select value={modality} onValueChange={(v) => setModality(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="search-modality" className="w-32 h-8 text-xs"><SelectValue placeholder="Modality" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modalities</SelectItem>
                {modalities.map((m) => (
                  <SelectItem key={m.key} value={m.key} textValue={m.label}>
                    <ModalityDotLabel modalityKey={m.key} label={m.label} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={tag || "all"} onValueChange={(v) => setTag(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="search-tag" className="w-36 h-8 text-xs"><SelectValue placeholder="Tag" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {tags.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Separator orientation="vertical" className="h-5 hidden sm:block" />
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1.5 text-xs text-muted-foreground w-full sm:w-auto">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 flex-shrink-0" /> From
                <Input data-testid="search-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-full sm:w-36 text-xs" />
              </div>
              <div className="flex items-center gap-1.5">
                To
                <Input data-testid="search-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-full sm:w-36 text-xs" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {searched && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {totalResults} result{totalResults !== 1 ? "s" : ""} found
            {query && <> for "<span className="text-foreground font-medium">{query}</span>"</>}
            {tag && <> tagged "<span className="text-foreground font-medium">{tag}</span>"</>}
          </p>

          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4 flex flex-wrap h-auto gap-1">
              <TabsTrigger value="all" data-testid="tab-all">All ({totalResults})</TabsTrigger>
              <TabsTrigger value="patients" data-testid="tab-patients">Patients ({results.patients.length})</TabsTrigger>
              <TabsTrigger value="studies" data-testid="tab-studies">Studies ({results.studies.length})</TabsTrigger>
              <TabsTrigger value="files" data-testid="tab-files">Files ({results.files.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <ScrollArea className="h-[calc(100vh-400px)]">
                <ResultSection title="Patients" icon={Users} items={results.patients} type="patient" navigate={navigate} />
                <ResultSection title="Studies" icon={FolderOpen} items={results.studies} type="study" navigate={navigate} />
                <ResultSection title="Files" icon={FileText} items={results.files} type="file" navigate={navigate} />
              </ScrollArea>
            </TabsContent>
            <TabsContent value="patients">
              <ScrollArea className="h-[calc(100vh-400px)]">
                <ResultSection title="Patients" icon={Users} items={results.patients} type="patient" navigate={navigate} />
              </ScrollArea>
            </TabsContent>
            <TabsContent value="studies">
              <ScrollArea className="h-[calc(100vh-400px)]">
                <ResultSection title="Studies" icon={FolderOpen} items={results.studies} type="study" navigate={navigate} />
              </ScrollArea>
            </TabsContent>
            <TabsContent value="files">
              <ScrollArea className="h-[calc(100vh-400px)]">
                <ResultSection title="Files" icon={FileText} items={results.files} type="file" navigate={navigate} />
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {!searched && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-secondary/60 flex items-center justify-center mb-4">
            <Search className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-1">Search & Discovery</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Search across patients, studies, and files. Use filters to narrow results by modality, tag, and date range.
          </p>
        </div>
      )}
    </div>
  );
}

function ResultSection({ title, icon: Icon, items, type, navigate }) {
  const { getModality } = useModalities();
  if (items.length === 0) return null;
  return (
    <div className="mb-6">
      <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" /> {title} ({items.length})
      </h3>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={item.id || i} data-testid={`result-${type}-${i}`}
               className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-transparent hover:border-border cursor-pointer transition-colors"
               onClick={() => {
                 if (type === "patient") navigate(`/patients/${item.uhid}`);
                 else if (type === "study") navigate(`/viewer/${item.id}`);
               }}>
            <div className="flex-1 min-w-0">
              {type === "patient" && (
                <div>
                  <span className="text-sm font-medium">{item.name}</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs font-mono text-primary">{formatPatientDisplayUhid(item)}</span>
                    <span className="text-xs text-muted-foreground">{item.age} yrs · {item.gender}</span>
                    <span className="text-xs text-muted-foreground">{item.organization}</span>
                  </div>
                  {(item.tags?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {item.tags.map((t) => (
                        <Badge key={t} variant="secondary" className="text-[10px] font-normal">{t}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {type === "study" && (
                <div>
                  <span className="text-sm font-medium">{item.description}</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge className={`text-[10px] ${getModalityBadgeClass(item.modality)}`}>
                      {getModality(item.modality)?.label ?? item.modality}
                    </Badge>
                    <span className="text-xs font-mono text-primary">{formatPatientDisplayUhid({ patient_uhid: item.patient_uhid })}</span>
                    <span className="text-xs text-muted-foreground">{item.study_date}</span>
                  </div>
                  {item.findings && <p className="text-xs text-muted-foreground mt-1 truncate">{item.findings}</p>}
                </div>
              )}
              {type === "file" && (
                <div>
                  <span className="text-sm font-medium">{item.original_filename}</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[10px] uppercase">{item.file_type}</Badge>
                    <span className="text-xs font-mono text-primary">{formatPatientDisplayUhid({ patient_uhid: item.patient_uhid })}</span>
                  </div>
                </div>
              )}
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}
